import { useDroppable } from "@dnd-kit/core";
import { cn } from "@/lib/utils";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import type { EventWithRelations } from "@/hooks/useEvents";
import type { EventStatus } from "@/types/database";
import { SortableEventCard } from "./SortableEventCard";

const STATUS_DESCRIPTIONS: Record<EventStatus, string> = {
  Project: "Événement en phase de réflexion, pas encore de contact établi.",
  "First Contact":
    "Premier échange avec l'organisateur, discussion des besoins.",
  "Documents Sent":
    "Devis et fiche technique envoyés, en attente de validation.",
  Validated: "Devis signé et acompte reçu - Événement confirmé.",
  "Final Organization": "Derniers préparatifs en cours, événement imminent.",
  Completed: "Événement terminé avec succès.",
  Abandoned: "Événement annulé ou abandonné.",
};

interface KanbanColumnProps {
  status: EventStatus;
  label: string;
  color: string;
  events: EventWithRelations[];
}

export function KanbanColumn({
  status,
  label,
  color,
  events,
}: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: status,
  });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex w-72 flex-shrink-0 flex-col rounded-lg bg-muted/50",
        isOver && "ring-2 ring-primary",
      )}
    >
      <div className="flex items-center gap-2 border-b p-3">
        <div className={cn("h-3 w-3 rounded-full", color)} />
        <h3 className="font-medium">{label}</h3>
        <HelpTooltip content={STATUS_DESCRIPTIONS[status]} />
        <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
          {events.length}
        </span>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto p-2">
        {events.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            Aucun événement
          </div>
        ) : (
          events.map((event) => (
            <SortableEventCard key={event.id} event={event} />
          ))
        )}
      </div>
    </div>
  );
}
